import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { create, all } from 'mathjs';
import { TOOLS } from '../Replan/constants/drawingTools';
import { DndContext, closestCenter } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ColorPicker from '../Replan/components/ColorPicker'; // <-- Add this import

import { ReactComponent as HideEyeIcon } from '../../svg/hide-eye-svgrepo-com.svg';
import { ReactComponent as ShowEyeIcon } from '../../svg/show-eye-watch-svgrepo-com.svg';
import { ReactComponent as AssemblyPicker } from '../../svg/color-picker-svgrepo-com.svg';


import SortableParamItem from './SortableParamItem';
import AssemblyQuickDropdown from './AssemblyQuickDropdown';
import {
  normalizeSymbols,
  toMathjsImperial,
  evaluateFormula,
  resolveAllParams,
  math,
} from './paramUtils';

// --- Helper function to convert a string to a deterministic hex color ---
function stringToHexColor(str) {
  if (typeof str !== 'string') {
    str = String(str);
  }
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = '#';
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xff;
    color += ('00' + value.toString(16)).substr(-2);
  }

  return color;
}

const ShapeListItem = ({
  shape,
  measurementData,
  isSelected,
  isIndeterminate,
  isExpanded,
  activeDetailView,
  onShapeSelect,
  onToggleExpand,
  onUpdateShape,
  onSaveAsAssembly,
  onLoadAssembly,
  saveStatus,
  loadStatus,
  count,
  shapeIds = [],
  stackIdentical = false,
  projectAssemblies = [], // <-- add this prop (default empty)
  firebase,
  sourceVersion,
  onCopyForeignShape,
  onDrawSameShape,
  onRestoreDrawSettings,
  selectedTool,
}) => {
  const { t } = useTranslation();

  // Translate built-in param keys, pass through user-defined keys as-is
  const getParamLabel = (key) => t(`shapeList.param_labels.${key}`, key);

  // State for managing parameter inputs, specific to this item
  const [paramInputs, setParamInputs] = useState({
    key: '',
    value: '',
  });
  const [editingParam, setEditingParam] = useState(null); // e.g., { key: 'Assembly', value: 'Test' }
  const [isAddingParam, setIsAddingParam] = useState(false);
  const checkboxRef = useRef(null); // <-- Add a ref for the checkbox

  const assemblyDisplayName = useMemo(() => {
    const asmValue = shape.params?.Assembly;
    if (!asmValue) return '';
    const match = projectAssemblies.find(
      (a) => a.id === asmValue || a.name === asmValue
    );
    return match ? (match.name || match.id) : asmValue;
  }, [shape.params?.Assembly, projectAssemblies]);

  // Editable assembly input allowing free text or selection from project assemblies
  const [assemblyInput, setAssemblyInput] = useState(assemblyDisplayName);
  const [isDrawActive, setIsDrawActive] = useState(false);
  useEffect(() => {
    setAssemblyInput(assemblyDisplayName);
  }, [assemblyDisplayName]);

  useEffect(() => {
    if (isDrawActive && selectedTool !== shape.type) {
      setIsDrawActive(false);
      onRestoreDrawSettings && onRestoreDrawSettings();
    }
  }, [selectedTool, shape.type, isDrawActive, onRestoreDrawSettings]);

  // --- Effect to manage checkbox state ---
  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.checked = isSelected && !isIndeterminate;
      checkboxRef.current.indeterminate = isIndeterminate;
    }
  }, [isSelected, isIndeterminate]);

  // The following state and refs are no longer needed with dnd-kit
  // const [isDragging, setIsDragging] = useState(false);
  // const dragItem = useRef();
  // const dragOverItem = useRef();

  // Formula evaluation and resolution are provided by shared utilities in ./paramUtils

  // --- Event Handlers ---

  // Helper: target ids to apply param changes to
  const targetShapeIds = useMemo(() => {
    return Array.isArray(shapeIds) && shapeIds.length
      ? shapeIds
      : [shape.id];
  }, [shapeIds, shape.id]);

  // Helper to apply params update to all shapes in the stack
  const applyParamsToStack = (paramsUpdater) => {
    if (!onUpdateShape) return;
    // Use representative shape.params as baseline
    const baseParams = shape.params || {};
    targetShapeIds.forEach((id) => {
      const updatedParams = paramsUpdater(baseParams, id);
      onUpdateShape(id, { params: updatedParams });
    });
  };

  // Assembly input handlers: allow typing a name not present in the list,
  // apply it to the stack and optionally load or save.
  const handleAssemblyApply = (name) => {
    const asmName =
      typeof name === 'string' && name.length
        ? name
        : assemblyInput || '';

    // Normalize input to match doc ID conventions for lookup
    const normalize = (n) =>
      firebase && typeof firebase.normalizeAssemblyId === 'function'
        ? firebase.normalizeAssemblyId(n)
        : n;
    const normalizedInput = normalize(asmName) || asmName;

    // Resolve name/ID to the assembly ID to store it
    // Match by: exact name, exact ID, or normalized name (for name-as-ID assemblies)
    const matched = projectAssemblies.find(
      (a) =>
        a.name === asmName ||
        a.id === asmName ||
        a.id === normalizedInput,
    );
    const resolvedId = matched ? matched.id : normalizedInput;

    if (resolvedId && onLoadAssembly) onLoadAssembly(resolvedId);
  };

  const handleAssemblySave = () => {
    const rawName = assemblyInput || shape.params?.Assembly || '';
    if (!onSaveAsAssembly || !rawName) return;

    // Normalize to match the doc ID that doCreateAssembly will produce
    const normalize = (n) =>
      firebase && typeof firebase.normalizeAssemblyId === 'function'
        ? firebase.normalizeAssemblyId(n)
        : n;
    const normalizedId = normalize(rawName) || rawName;

    // Set Assembly param to the normalized name (which equals the doc ID)
    applyParamsToStack((baseParams) => ({
      ...(baseParams || {}),
      Assembly: normalizedId,
    }));
    // Call save handler with the normalized ID
    onSaveAsAssembly(normalizedId);
  };

  const handleTagAdd = (newTag, existingTags = []) => {
    if (!onUpdateShape || !newTag.trim()) return;
    // Use rep tags as baseline and propagate
    applyParamsToStack((baseParams) => {
      const repTags = baseParams.tags || existingTags || [];
      const updatedTags = [...repTags, newTag.trim()];
      return { ...baseParams, tags: updatedTags };
    });
  };

  const handleTagRemove = (tagToRemove, existingTags = []) => {
    if (!onUpdateShape) return;
    applyParamsToStack((baseParams) => {
      const repTags = baseParams.tags || existingTags || [];
      const updatedTags = repTags.filter(
        (tag) => tag !== tagToRemove,
      );
      return { ...baseParams, tags: updatedTags };
    });
  };

  const handleParamAdd = (existingParams = {}) => {
    const { key, value } = paramInputs;
    if (
      !onUpdateShape ||
      !key ||
      !key.trim() ||
      !value ||
      !value.trim()
    )
      return;

    applyParamsToStack((baseParams) => {
      const updatedParams = {
        ...(baseParams || {}),
        [key.trim()]: value.trim(),
      };
      const updatedOrder =
        baseParams && baseParams.order
          ? [...baseParams.order, key.trim()]
          : [key.trim()];
      return { ...updatedParams, order: updatedOrder };
    });

    setParamInputs({ key: '', value: '' });
    setIsAddingParam(false);
  };

  const handleParamRemove = (keyToRemove, existingParams = {}) => {
    if (!onUpdateShape) return;

    applyParamsToStack((baseParams) => {
      const {
        [keyToRemove]: _,
        order,
        ...rest
      } = baseParams || existingParams || {};
      const updatedOrder = order
        ? order.filter((k) => k !== keyToRemove)
        : [];
      return { ...rest, order: updatedOrder };
    });
  };

  const handleParamUpdate = (key, newValue, existingParams = {}) => {
    if (!onUpdateShape) return;

    applyParamsToStack((baseParams) => {
      const updatedParams = {
        ...(baseParams || existingParams || {}),
        [key]: newValue.trim(),
      };
      return updatedParams;
    });

    // Add this line to reset editing mode after the update
    setEditingParam(null);
  };

  // NOTE: vertex changes are geometry edits — keep per-shape (no propagation) unless desired
  const handleVertexChange = (vertexIndex, axis, value) => {
    if (!onUpdateShape) return;
    const numericValue = parseFloat(value);
    if (isNaN(numericValue)) return;
    const newPoints = [...shape.points];
    const pointIndex = vertexIndex * 2 + (axis === 'x' ? 0 : 1);
    if (pointIndex < newPoints.length) {
      newPoints[pointIndex] = numericValue;
      onUpdateShape(shape.id, { points: newPoints });
    }
  };

  // This is the new drag end handler for dnd-kit
  const handleDragEnd = (event) => {
    const { active, over } = event;

    // Do not allow re-grouping via drag-and-drop while items are stacked.
    if (!over || active.id === over.id || stackIdentical) {
      return;
    }

    // Use this shape's params.order (the SortableContext items) to compute new order
    const existingParams = shape.params || {};
    const orderedKeys = existingParams.order || [];

    const oldIndex = orderedKeys.indexOf(active.id);
    const newIndex = orderedKeys.indexOf(over.id);

    // If either id isn't an ordered key, nothing to do
    if (oldIndex === -1 || newIndex === -1) return;

    const updatedOrder = arrayMove(orderedKeys, oldIndex, newIndex);

    // Persist the updated order back to this shape's params
    if (onUpdateShape) {
      onUpdateShape(shape.id, {
        params: { ...existingParams, order: updatedOrder },
      });
    }
  };

  // --- Effect to update shape color from 'Color' param ---
  useEffect(() => {
    // Update color whenever params change, regardless of expanded state
    const calculatedParams = {};
    if (shape.type === TOOLS.POLYLINE || shape.type === TOOLS.LINE || shape.type === TOOLS.MEASURE || shape.type === TOOLS.COTATION || shape.type === TOOLS.ARROW)
      calculatedParams['Total Length'] =
        measurementData.summary
          .replace('Total Length: ', '')
          .replace('Total: ', '')
          .replace('Length: ', '');
    if (shape.type === TOOLS.AREA)
      calculatedParams['Total Area'] = measurementData.summary
        .replace('Net Area: ', '')
        .replace('Total Area: ', '')
        .replace('Area: ', '');
    if (shape.type === TOOLS.POINT) {
      const diameterDetail = measurementData?.details?.find(d => d.startsWith('Diameter:'));
      if (diameterDetail) {
        calculatedParams['Diameter'] = diameterDetail.replace('Diameter: ', '');
      } else {
        const radius = shape.radius || 0;
        calculatedParams['Diameter'] = (radius * 2).toFixed(2);
      }
    }

    const allResolvedParams = resolveAllParams(
      shape.params,
      calculatedParams,
    );
    const colorParamValue = allResolvedParams['Color'];

    if (colorParamValue !== undefined && !colorParamValue.error) {
      let displayValue = colorParamValue;
      if (displayValue instanceof math.Unit) {
        displayValue = math.format(displayValue, {
          notation: 'fixed',
          precision: 1,
        });
      } else if (typeof displayValue === 'number') {
        displayValue = displayValue.toFixed(1);
      }

      const rawValue = shape.params?.Color || shape.stroke || '';
      const isHexColor = /^#[0-9A-F]{6}$/i.test(displayValue);

      if (rawValue.startsWith('=') && !isHexColor) {
        displayValue = stringToHexColor(String(displayValue));
      }

      if (displayValue && displayValue !== shape.stroke) {
        targetShapeIds.forEach((id) =>
          onUpdateShape(id, { stroke: displayValue }),
        );
      }
    }
  }, [
    shape.params,
    shape.stroke,
    onUpdateShape,
    measurementData.summary,
    shape.type,
    targetShapeIds,
  ]);

  const hasDetails =
    measurementData.details && measurementData.details.length > 0;

  return (
    <div className="shape-list-item">
      <div
        className="shape-list-header"
        style={{
          background: isExpanded
            ? 'var(--pico-form-element-selected-background-color)'
            : 'transparent',
        }}
      >
        <input
          ref={checkboxRef}
          className="shape-list-item-checkbox"
          type="checkbox"
          id={`shape-checkbox-${shape.id}`}
          onChange={onShapeSelect}
        />
        <span
          onClick={() => {
            if (!onUpdateShape) return;
            // Toggle visibility for all shapes in this stack
            targetShapeIds.forEach((id) =>
              onUpdateShape(id, { visible: !shape.visible }),
            );
          }}
          className="shape-list-visibility-toggle"
          title={t('shapeList.toggle_visibility')}
          style={{
            backgroundColor: shape.stroke,
            opacity: shape.visible ? 1 : 0.5,
            scale: isSelected ? '1' : '0.9',
          }}
        >
          {shape.visible ? (
            <ShowEyeIcon className="tool-icon" />
          ) : (
            <HideEyeIcon className="tool-icon" />
          )}
        </span>
        <label
          htmlFor={`shape-checkbox-${shape.id}`}
          className="shape-main-label"
          title={`ID: ${shape.id}\nColor: ${shape.stroke}\n${measurementData.summary}`}
        >
          <div className="shape-info">
            <span
              className={
                isSelected ? 'shape-type selected' : 'shape-type'
              }
            >
              <u>{count > 1 && `${count} x`} </u>
              {assemblyDisplayName || (
                shape.type === TOOLS.POINT && shape.pointMarker && shape.pointMarker !== 'disk'
                  ? `${t(`replan.shape_types.${shape.type}`, shape.type)} / ${t(`replan.point_markers.${shape.pointMarker}`, shape.pointMarker)}`
                  : t(`replan.shape_types.${shape.type}`, shape.type)
              )}
              {sourceVersion && (
                <span
                  className="shape-list-version-badge"
                  title={`From version: ${sourceVersion}`}
                >
                  {sourceVersion}
                </span>
              )}
            </span>
            {[TOOLS.AREA, TOOLS.POLYLINE, TOOLS.MEASURE, TOOLS.COTATION, TOOLS.LINE, TOOLS.ARROW].includes(shape.type) && <div className="shape-measurements">
              {measurementData.summary.replace('Total: ', '').replaceAll('Area:', '')}
            </div>}
          </div>
        </label>
        {hasDetails && (
          <AssemblyQuickDropdown
            className="assembly-quick-select"
            assemblies={projectAssemblies}
            onApply={(id) => {
              if (id && onLoadAssembly) {
                handleAssemblyApply(id);
              }
            }}
            onClick={(e) => e.stopPropagation()}
            title={t('shapeList.quick_load_assembly')}
          />
        )}

        {hasDetails && onDrawSameShape && (
          <button
            className="assembly-quick-select draw-same"
            style={isDrawActive ? { opacity: 1 } : undefined}
            onClick={(e) => {
              e.stopPropagation();
              setIsDrawActive(true);
              onDrawSameShape(shape);
            }}
            title={t('shapeList.draw_same_shape')}
          >
            <AssemblyPicker className="tool-icon" />
          </button>
        )}

        {sourceVersion && (
          <button
            className="expand-button copy-foreign"
            onClick={(e) => {
              e.stopPropagation();
              if (onCopyForeignShape) onCopyForeignShape(shape);
            }}
            title={t('shapeList.copy_to_current_version')}
          >
            ↓
          </button>
        )}
        {hasDetails && (
          <button
            className="expand-button"
            onClick={() => onToggleExpand(shape.id)}
            title={isExpanded ? t('shapeList.hide_details') : t('shapeList.show_details')}
          >
            {isExpanded ? '-' : '+'}
          </button>
        )}
      </div>

      {
        hasDetails && isExpanded && (
          <div className="detail-content">
            {/* --- Tags View --- */}
            {activeDetailView === 'tags' && (
              <>
                <div className="shape-tags-container">
                  {shape.tags &&
                    shape.tags.map((tag, index) => (
                      <span key={index} className="shape-tag">
                        {tag}
                        <button
                          className="remove-tag-button"
                          onClick={() =>
                            handleTagRemove(tag, shape.tags)
                          }
                        >
                          &times;
                        </button>
                      </span>
                    ))}
                </div>
                <input
                  type="text"
                  className="shape-tags-input"
                  placeholder={t('shapeList.add_tag_placeholder')}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleTagAdd(e.target.value, shape.tags);
                      e.target.value = '';
                    }
                  }}
                />
              </>
            )}

            {/* --- Measurements View --- */}
            {activeDetailView === 'measurements' && (
              <div className="shape-details">
                {measurementData.details.map((detail, index) => (
                  <div
                    key={index}
                    className={
                      detail.startsWith('---')
                        ? 'detail-section'
                        : 'detail-item'
                    }
                  >
                    {detail}
                  </div>
                ))}
              </div>
            )}

            {/* --- Params View --- */}
            {activeDetailView === 'params' &&
              (() => {
                // Added Color param (initial state from shape.stroke)
                const defaultParamsToShow = {
                  Assembly: shape.params?.Assembly || '',
                  Reference: shape.params?.Reference || '',
                  Color:
                    shape.params?.Color || shape.stroke || '#000000',
                };
                const calculatedParams = {};
                if (shape.type === TOOLS.POLYLINE)
                  calculatedParams['Total Length'] =
                    measurementData.summary.replace('Total: ', '');
                if (shape.type === TOOLS.AREA)
                  calculatedParams['Total Area'] =
                    measurementData.summary
                      .replace('Net Area: ', '')
                      .replace('Area: ', '');
                if (shape.type === TOOLS.POINT) {
                  const diameterDetail = measurementData?.details?.find(d => d.startsWith('Diameter:'));
                  if (diameterDetail) {
                    calculatedParams['Diameter'] = diameterDetail.replace('Diameter: ', '');
                  } else {
                    const radius = shape.radius || 0;
                    calculatedParams['Diameter'] = (radius * 2).toFixed(2);
                  }
                }

                const allResolvedParams = resolveAllParams(
                  shape.params,
                  calculatedParams,
                );
                const userParams = { ...shape.params };
                delete userParams.Assembly;
                delete userParams.Reference;
                delete userParams.Color;

                // Helper to format the final display value
                const formatDisplayValue = (value) => {
                  if (value?.error) return value.error;
                  if (value instanceof math.Unit) {
                    return math.format(value, {
                      notation: 'fixed',
                      precision: 2,
                    }).replace(/\^2/g, '²').replace(/\bft2\b/g, 'ft²').replace(/\bin2\b/g, 'in²');
                  }
                  if (typeof value === 'number')
                    return value.toFixed(2);
                  return value;
                };

                const orderedKeys =
                  shape.params?.order ||
                  Object.keys(userParams).filter((k) => k !== 'order');

                return (
                  <div className="shape-params-container">
                    {/* Default and Calculated Params (not sortable) */}
                    {Object.entries({
                      ...defaultParamsToShow,
                      ...calculatedParams,
                    }).map(([key, value]) => {
                      const isCalculated = key in calculatedParams;
                      const isEditing = editingParam?.key === key;
                      // Color is a shape property, show from shape.stroke and allow changing it
                      if (key === 'Color') {
                        let displayValue = formatDisplayValue(
                          allResolvedParams[key],
                        );
                        const rawValue =
                          shape.params?.[key] || shape.stroke || '';

                        // If the value is calculated (from a formula) and not a valid hex color, convert it.
                        const isHexColor = /^#[0-9A-F]{6}$/i.test(
                          displayValue,
                        );
                        if (
                          rawValue?.startsWith?.('=') &&
                          !isHexColor &&
                          !displayValue?.error
                        ) {
                          displayValue = stringToHexColor(
                            String(displayValue),
                          );
                        }

                        // Ensure we have a valid CSS color to show in the swatch.
                        const resolvedColor =
                          typeof displayValue === 'string' &&
                            /^#[0-9A-F]{6}$/i.test(displayValue)
                            ? displayValue
                            : typeof shape.stroke === 'string' &&
                              /^#[0-9A-F]{6}$/i.test(shape.stroke)
                              ? shape.stroke
                              : stringToHexColor(
                                String(
                                  displayValue ||
                                  shape.stroke ||
                                  '#000000',
                                ),
                              );

                        return (
                          <div key={key} className="shape-param-item">
                            <span className="shape-param-key">
                              {getParamLabel(key)}:
                            </span>
                            <div
                              className="shape-param-value-container"
                            >
                              <span
                                className="shape-param-value read-only"
                                title={
                                  typeof rawValue === 'string' &&
                                    rawValue.startsWith('=')
                                    ? `Formula: ${rawValue}`
                                    : ''
                                }
                              >
                                {formatDisplayValue(
                                  allResolvedParams[key],
                                ) || t('shapeList.empty')}
                              </span>
                              <ColorPicker
                                value={resolvedColor}
                                onChange={(newColor) => {
                                  handleParamUpdate(
                                    key,
                                    newColor,
                                    shape.params,
                                  );
                                  setEditingParam(null);
                                }}
                                title={t('shapeList.select_color')}
                                autoOpen={false}
                                size="small"
                              />
                            </div>
                          </div>
                        );
                      }

                      // NEW: render Assembly as a select (replace editable input + separate load button)
                      if (key === 'Assembly') {
                        return (
                          <div key={key} className="shape-param-item">
                            <span className="shape-param-key">
                              {getParamLabel(key)}:
                            </span>

                            <input
                              list={`assembly-list-${shape.id}`}
                              className="shape-param-assembly-input"
                              value={assemblyInput}
                              onChange={(e) =>
                                setAssemblyInput(e.target.value)
                              }
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleAssemblyApply(assemblyInput);
                                }
                              }}
                              onClick={(e) => e.stopPropagation()}
                              title={t('shapeList.assembly_input_hint')}
                            />

                            <datalist id={`assembly-list-${shape.id}`}>
                              <option value="">
                                {shape.params?.Assembly ||
                                  t('shapeList.choose_assembly')}
                              </option>
                              {projectAssemblies.map((a) => (
                                <option key={a.id} value={a.name || a.id}>
                                  {a.name || a.id}
                                </option>
                              ))}
                            </datalist>

                            <button
                              className="shapelist-toggle-button"
                              onClick={() =>
                                handleAssemblyApply(assemblyInput)
                              }
                              disabled={
                                loadStatus === 'loading' ||
                                !assemblyInput
                              }
                              title={t('shapeList.load_assembly')}
                            >
                              {loadStatus === 'loading'
                                ? '⏳'
                                : loadStatus === 'loaded'
                                  ? '👍'
                                  : '📨'}
                            </button>

                            <button
                              className="shapelist-toggle-button"
                              onClick={() => handleAssemblySave()}
                              disabled={saveStatus === 'saving'}
                              title={
                                projectAssemblies.some(
                                  (a) => a.name === assemblyInput || a.id === assemblyInput,
                                )
                                  ? t('shapeList.update_assembly')
                                  : t('shapeList.save_as_assembly')
                              }
                              aria-label={
                                projectAssemblies.some(
                                  (a) => a.name === assemblyInput || a.id === assemblyInput,
                                )
                                  ? t('shapeList.update_assembly')
                                  : t('shapeList.save_as_assembly')
                              }
                            >
                              {saveStatus === 'saving'
                                ? '⏳'
                                : saveStatus === 'saved'
                                  ? '👍'
                                  : '💾'}
                            </button>

                            {!isCalculated && (
                              <button
                                className="remove-tag-button"
                                onClick={() =>
                                  handleParamRemove(key, shape.params)
                                }
                              >
                                &times;
                              </button>
                            )}
                          </div>
                        );
                      }

                      // existing (non-Assembly) param rendering
                      const displayValue = formatDisplayValue(
                        allResolvedParams[key],
                      );

                      return (
                        <div key={key} className="shape-param-item">
                          <span className="shape-param-key">
                            {getParamLabel(key)}:
                          </span>
                          {isEditing && !isCalculated ? (
                            <input
                              type="text"
                              className="shape-param-value-input"
                              value={editingParam.value}
                              onChange={(e) =>
                                setEditingParam({
                                  ...editingParam,
                                  value: e.target.value,
                                })
                              }
                              onBlur={() =>
                                handleParamUpdate(
                                  key,
                                  editingParam.value,
                                  shape.params,
                                )
                              }
                              onKeyDown={(e) => {
                                if (e.key === 'Enter')
                                  handleParamUpdate(
                                    key,
                                    editingParam.value,
                                    shape.params,
                                  );
                                if (e.key === 'Escape')
                                  setEditingParam(null);
                              }}
                              autoFocus
                            />
                          ) : (
                            <span
                              className={`shape-param-value ${isCalculated ? 'read-only' : ''}`}
                              onClick={() =>
                                !isCalculated &&
                                setEditingParam({
                                  key,
                                  value: shape.params?.[key] || '',
                                })
                              }
                              title={
                                typeof value === 'string' &&
                                  value.startsWith('=')
                                  ? `Formula: ${value}`
                                  : ''
                              }
                            >
                              {displayValue ||
                                (isCalculated ? '...' : t('shapeList.empty'))}
                            </span>
                          )}
                          {!isCalculated && (
                            <button
                              className="remove-tag-button"
                              onClick={() =>
                                handleParamRemove(key, shape.params)
                              }
                            >
                              &times;
                            </button>
                          )}
                        </div>
                      );
                    })}

                    {/* User-Defined Params (sortable) */}
                    <DndContext
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={orderedKeys}
                        strategy={verticalListSortingStrategy}
                      >
                        {orderedKeys.map((key) => {
                          if (
                            key === 'Assembly' ||
                            key === 'Reference' ||
                            key === 'Color'
                          )
                            return null;
                          const value = userParams[key];
                          if (value === undefined) return null;

                          const isEditing = editingParam?.key === key;
                          const displayValue = formatDisplayValue(
                            allResolvedParams[key],
                          );

                          return (
                            <SortableParamItem key={key} id={key}>
                              <div className="shape-param-item">
                                <span
                                  className="shape-param-key draggable"
                                >
                                  {getParamLabel(key)}:
                                </span>
                                {isEditing ? (
                                  <input
                                    type="text"
                                    className="shape-param-value-input"
                                    value={editingParam.value}
                                    onChange={(e) =>
                                      setEditingParam({
                                        ...editingParam,
                                        value: e.target.value,
                                      })
                                    }
                                    onBlur={() =>
                                      handleParamUpdate(
                                        key,
                                        editingParam.value,
                                        shape.params,
                                      )
                                    }
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter')
                                        handleParamUpdate(
                                          key,
                                          editingParam.value,
                                          shape.params,
                                        );
                                      if (e.key === 'Escape')
                                        setEditingParam(null);
                                    }}
                                    autoFocus
                                  />
                                ) : (
                                  <span
                                    className="shape-param-value"
                                    onClick={() =>
                                      setEditingParam({ key, value })
                                    }
                                    title={
                                      typeof value === 'string' &&
                                        value.startsWith('=')
                                        ? `Formula: ${value}`
                                        : ''
                                    }
                                  >
                                    {displayValue || t('shapeList.empty')}
                                  </span>
                                )}
                                <button
                                  className="remove-tag-button"
                                  onClick={() =>
                                    handleParamRemove(key, shape.params)
                                  }
                                >
                                  &times;
                                </button>
                              </div>
                            </SortableParamItem>
                          );
                        })}
                      </SortableContext>
                    </DndContext>

                    {isAddingParam && (
                      <div className="shape-param-input-group">
                        <input
                          type="text"
                          className="shape-param-input"
                          placeholder={t('shapeList.param_key_placeholder')}
                          value={paramInputs.key}
                          onChange={(e) =>
                            setParamInputs({
                              ...paramInputs,
                              key: e.target.value,
                            })
                          }
                        />
                        <input
                          type="text"
                          className="shape-param-input"
                          placeholder={t('shapeList.param_value_placeholder')}
                          value={paramInputs.value}
                          onChange={(e) =>
                            setParamInputs({
                              ...paramInputs,
                              value: e.target.value,
                            })
                          }
                          onKeyDown={(e) => {
                            if (e.key === 'Enter')
                              handleParamAdd(shape.params);
                          }}
                        />
                        <button
                          className="add-param-button"
                          onClick={() => handleParamAdd(shape.params)}
                        >
                          {t('shapeList.save')}
                        </button>
                      </div>
                    )}
                    <button
                      className="add-param-toggle-button"
                      onClick={() => setIsAddingParam(!isAddingParam)}
                    >
                      {isAddingParam
                        ? t('shapeList.cancel')
                        : t('shapeList.add_custom_parameter')}
                    </button>
                  </div>
                );
              })()}

            {/* --- Vertices View --- */}
            {activeDetailView === 'vertices' && (
              <div className="vertex-editor">
                <div className="detail-section">{t('shapeList.vertices')}</div>
                <table>
                  <thead>
                    <tr>
                      <th>{t('shapeList.point')}</th>
                      <th>{t('shapeList.x_axis')}</th>
                      <th>{t('shapeList.y_axis')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shape.points &&
                      Array.from({
                        length: shape.points.length / 2,
                      }).map((_, index) => (
                        <tr key={index}>
                          <td>P{index + 1}</td>
                          <td>
                            <input
                              type="number"
                              className="vertex-input"
                              value={Math.round(
                                shape.points[index * 2],
                              )}
                              onChange={(e) =>
                                handleVertexChange(
                                  index,
                                  'x',
                                  e.target.value,
                                )
                              }
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              className="vertex-input"
                              value={Math.round(
                                shape.points[index * 2 + 1],
                              )}
                              onChange={(e) =>
                                handleVertexChange(
                                  index,
                                  'y',
                                  e.target.value,
                                )
                              }
                            />
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )
      }
    </div >
  );
};

export default ShapeListItem;
